import { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SearchInput from "../../input/SearchInput.jsx";
import DataTable from "../../core/DataTable.jsx";
import Loading from "../../layout/Loading.jsx";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import { useAuthInfo } from "../../../context/AuthContext";

import { errorToast, successfulToast } from "../../../util/toastNotifications";
import { truncateName } from "../../../util/stringUtils";
import { toSixDigitHex } from "../../../util/ColorUtil.js";

const tagsEndpoint = "/tags?limit=10&page=1";

const ColorPicker = ({ color, onChange, tagId }) => {
  const handleChange = (e) => {
    e.stopPropagation();
    onChange(tagId, e.target.value);
  };

  const handleInput = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="tag-color-picker-wrapper" style={{ position: "relative" }}>
      <input
        type="color"
        value={color}
        onChange={handleChange}
        onInput={handleInput}
        style={{
          width: "32px",
          height: "32px",
          padding: 0,
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

const TagsPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [editingTag, setEditingTag] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editingColor, setEditingColor] = useState(null);
  const [editedColor, setEditedColor] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const { userInfo } = useAuthInfo();

  const isSuperAdmin = userInfo?.role?.name === "super-admin";

  const searchDebounce = useDebounce(searchTerm);
  const { fetchData: fetchListData, data, isLoading } = useTriggeredAPICall();
  const { fetchData } = useTriggeredAPICall();

  const tagsBaseUrl = useMemo(() => {
    const base = searchDebounce
      ? `/tags/search?q=${encodeURIComponent(searchDebounce)}`
      : "/tags";
    if (filterStatus === "all") return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}is_active=${filterStatus === "active"}`;
  }, [searchDebounce, filterStatus]);

  const { currentData, recordsData, listPage } = usePagination(
    data,
    tagsBaseUrl,
    fetchListData
  );

  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    setLocalData(currentData);
  }, [currentData]);

  const handleStartEdit = (tag) => {
    setEditingTag(tag.tag_id);
    setEditedName(tag.name);
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    setEditedName("");
  };

  const handleSaveTag = async (tagId) => {
    try {
      const name = editedName.trim().slice(0, 30);
      await fetchData(`/tag/${tagId}`, "PUT", { name });
      successfulToast("Tag updated successfully");
      setEditingTag(null);
      setEditedName("");
      fetchListData(
        `${tagsBaseUrl}${tagsBaseUrl.includes("?") ? "&" : "?"}page=1`
      );
    } catch (error) {
      errorToast("Failed to update tag");
      console.error("Update Tag:", error);
    }
  };

  const handleStartColorEdit = (tag) => {
    setEditingColor(tag.tag_id);
    setEditedColor(toSixDigitHex(tag.color));
  };

  const handleCancelColorEdit = () => {
    setEditingColor(null);
    setEditedColor("");
  };

  const handleSaveColor = async (tagId) => {
    try {
      const withHash = editedColor.startsWith("#")
        ? editedColor
        : `#${editedColor}`;
      if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash)) {
        errorToast("Invalid hex color. Must be #RGB or #RRGGBB.");
        return;
      }
      const expanded = toSixDigitHex(withHash);
      const res = await fetchData(`/tag/${tagId}`, "PUT", { color: expanded });
      successfulToast("Tag color updated successfully");
      setEditingColor(null);
      setEditedColor("");
      setLocalData((prev) =>
        prev.map((tag) =>
          tag.tag_id === tagId ? { ...tag, color: expanded } : tag
        )
      );
    } catch (error) {
      errorToast("Failed to update tag color");
      console.error("Update Tag Color:", error);
    }
  };

  const handleColorChange = (tagId, color) => {
    setEditingColor(tagId);
    setEditedColor(color);
  };

  const handleToggleStatus = async (tag) => {
    const nextActive = !tag.is_active;
    setLocalData((prev) =>
      prev.map((t) =>
        t.tag_id === tag.tag_id ? { ...t, is_active: nextActive } : t
      )
    );
    try {
      await fetchData(`/tag/${tag.tag_id}`, "PUT", { is_active: nextActive });
      successfulToast(`Tag ${nextActive ? "activated" : "deactivated"}`);
      if (filterStatus !== "all") {
        fetchListData(
          `${tagsBaseUrl}${tagsBaseUrl.includes("?") ? "&" : "?"}page=1`
        );
      }
    } catch (error) {
      setLocalData((prev) =>
        prev.map((t) =>
          t.tag_id === tag.tag_id ? { ...t, is_active: tag.is_active } : t
        )
      );
      errorToast("Failed to update tag status");
      console.error("Update Tag Status:", error);
    }
  };

  const defaultSortFieldId = isSuperAdmin ? "organization" : "name";

  const columns = useMemo(
    () => [
      {
        id: "name",
        name: "Name",
        selector: (row) => row.name,
        width: "300px",
        sortable: true,
        sortFunction: (a, b) => {
          const aName = a.name.toLowerCase().trim();
          const bName = b.name.toLowerCase().trim();
          return aName.localeCompare(bName, "en", { sensitivity: "base" });
        },
        cell: (row) =>
          editingTag === row.tag_id ? (
            <div
              className="tag-edit-container"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: 0,
                padding: 0,
              }}
            >
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                maxLength={30}
                className="tag-edit-input"
                autoFocus
                style={{ flex: 1 }}
              />
              <button
                className="small save-button"
                disabled={editedName === row.name}
                onClick={() => handleSaveTag(row.tag_id)}
              >
                Save
              </button>
              <button
                className="small cancel-button"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="tag-name" onClick={() => handleStartEdit(row)}>
              {truncateName(row.name)}
            </div>
          ),
      },
      ...(isSuperAdmin
        ? [
            {
              id: "organization",
              name: "Organization",
              selector: (row) => row.organization?.name ?? "",
              sortable: true,
              sortFunction: (a, b) => {
                const aName = (a.organization?.name ?? "").toLowerCase().trim();
                const bName = (b.organization?.name ?? "").toLowerCase().trim();
                return aName.localeCompare(bName, "en", {
                  sensitivity: "base",
                });
              },
              width: "200px",
            },
          ]
        : []),
      {
        id: "color",
        name: "Color",
        selector: (row) => row.color,
        sortable: true,
        sortFunction: (a, b) => {
          const aColor = (a.color ?? "").toLowerCase().trim();
          const bColor = (b.color ?? "").toLowerCase().trim();
          return aColor.localeCompare(bColor, "en", { sensitivity: "base" });
        },
        width: "300px",
        cell: (row) => (
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              margin: 0,
              padding: 0,
            }}
          >
            {editingColor === row.tag_id ? (
              <div
                className="tag-edit-container"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  margin: 0,
                  padding: 0,
                }}
              >
                <input
                  type="text"
                  value={editedColor}
                  onChange={(e) => setEditedColor(e.target.value)}
                  className="tag-edit-input"
                  autoFocus
                  style={{
                    flex: 1,
                    minWidth: "120px",
                    margin: 0,
                    padding: "4px 8px",
                    height: "24px",
                  }}
                  placeholder="#000000"
                />
                <button
                  className="small save-button"
                  disabled={editedColor === row.color}
                  onClick={() => handleSaveColor(row.tag_id)}
                >
                  Save
                </button>
                <button
                  className="small cancel-button"
                  onClick={handleCancelColorEdit}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <ColorPicker
                  color={
                    /^#[0-9a-fA-F]{6}$/.test(toSixDigitHex(row.color))
                      ? toSixDigitHex(row.color)
                      : "#000000"
                  }
                  onChange={handleColorChange}
                  tagId={row.tag_id}
                />
                <span
                  style={{ fontFamily: "monospace", cursor: "pointer" }}
                  onClick={() => handleStartColorEdit(row)}
                >
                  {toSixDigitHex(row.color)}
                </span>
              </>
            )}
          </div>
        ),
      },
      {
        id: "is_active",
        name: "Status",
        selector: (row) => row.is_active,
        sortable: true,
        sortFunction: (a, b) =>
          a.is_active === b.is_active ? 0 : a.is_active ? 1 : -1,
        width: "140px",
        cell: (row) => (
          <label
            className="switch tag-status-switch"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row.is_active}
              onChange={() => handleToggleStatus(row)}
            />
            <span className="slider round">
              <span>On</span>
              <span>Off</span>
            </span>
          </label>
        ),
      },
    ],
    [
      editingTag,
      editedName,
      editingColor,
      editedColor,
      isSuperAdmin,
      tagsBaseUrl,
    ]
  );

  useEffect(() => {
    const sep = tagsBaseUrl.includes("?") ? "&" : "?";
    fetchListData(`${tagsBaseUrl}${sep}page=1`);
  }, [tagsBaseUrl, fetchListData]);

  if (isLoading && !localData.length) {
    return <Loading />;
  }

  return (
    <div className="flex-align-start width-full">
      <h2>
        <FontAwesomeIcon icon="fa-solid fa-tags" /> Tags
      </h2>
      <div className="flex-rows flex-align-center">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="width-200px"
          placeholder="Search by name, color, or organization..."
        />
        <label>
          <input
            type="checkbox"
            checked={filterStatus === "all"}
            onChange={() => setFilterStatus("all")}
          />
          <span className="ml-2 mr-4">All</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={filterStatus === "active"}
            onChange={() => setFilterStatus("active")}
          />
          <span className="ml-2 mr-4">Active</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={filterStatus === "inactive"}
            onChange={() => setFilterStatus("inactive")}
          />
          <span className="ml-2 mr-4">Inactive</span>
        </label>
      </div>
      <Loading isLoading={isLoading}>
        <DataTable
          columns={columns}
          data={localData}
          loading={isLoading}
          defaultSortFieldId={defaultSortFieldId}
          defaultSortAsc={true}
          recordsData={recordsData}
          onPageChange={listPage}
          baseUrl={tagsBaseUrl}
        />
      </Loading>
    </div>
  );
};

export default TagsPage;
